import {useEditor, EditorContent} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import './ArrRichTextEditor.scss';
import {Toolbar} from './Toolbar';
import {Indent} from './Indent';
import {Box} from '@mui/material';
import ArrButton from '../arr-button/ArrButton';
import {t} from 'i18next';
import {useEffect, useState} from 'react';
import ArrModal from '../arr-modal/ArrModal';
import ArrSingleSelectDropdown from '../arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../shared/types';
import {useGetMetadataFields} from '../../../hooks/documentPrinting/useDocumentPrinting';

interface IArrRichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  dataTestId: string;
}

export default function ArrRichTextEditor({
  value = '',
  onChange,
  dataTestId,
}: IArrRichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Indent,
    ],
    content: value,
    onUpdate({editor}) {
      const html = editor.getHTML();
      const text = editor.getText();
      if (text.trim() !== '') {
        onChange?.(html);
      } else {
        onChange?.('');
      }
    },
    editorProps: {
      attributes: {
        dir: 'ltr',
        style: 'text-align: left;',
        class: 'ProseMirror simple-editor',
        resize: 'vertical',
      },
    },
  });

  if (!editor) return null;

  const [openInsertDialog, setOpenInsertDialog] = useState<boolean>(false);
  const [selectedMetadataField, setSelectedMetadataField] =
    useState<ISelectItem | null>(null);
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const {data: metadataFieldList} = useGetMetadataFields(openInsertDialog);

  const insertMetadataField = () => {
    if (!editor || !selectedMetadataField) return;
    editor
      .chain()
      .focus()
      .insertContent(`$${selectedMetadataField.fieldLabel}$`)
      .run();
    setOpenInsertDialog(false);
  };

  useEffect(() => {
    if (!editor) return;

    if (isHtmlMode) {
      const html = editor.getHTML();
      editor.commands.setContent(`<code>${escapeHtml(html)}</code>`);
    } else {
      const text = editor.getText();
      editor.commands.setContent(text);
    }
  }, [isHtmlMode]);

  const escapeHtml = (html: string) =>
    html.replace(/</g, '&lt;').replace(/>/g, '&gt;');

  useEffect(() => {
    if (
      metadataFieldList &&
      metadataFieldList.length > 0 &&
      !selectedMetadataField
    ) {
      setSelectedMetadataField(metadataFieldList[0] as unknown as ISelectItem);
    }
  }, [selectedMetadataField, metadataFieldList]);

  return (
    <div
      className="editor-container"
      data-testid={`arr-rich-text-editor-insert-metadata-field`}
      tabIndex={0}
      >
      <Toolbar
        editor={editor}
        isHtmlMode={isHtmlMode}
        onToggleHtmlMode={setIsHtmlMode}
        
      />
      <EditorContent
        editor={editor}
        className={`editor-content ${isHtmlMode ? 'html-mode' : ''}`}
        tabIndex={0}
      />
      <Box className="editor-footer">
        <ArrButton
          variant="text"
          size="medium"
          onClick={() => {
            setOpenInsertDialog(true);
          }}
          disabled={false}
          aria-label={t('richTextEditor.insertMetadataField')}
          dataTestId={`insertMetadataField-button-${dataTestId}`}
        >
          {t('richTextEditor.insertMetadataField')}
        </ArrButton>
      </Box>
      <ArrModal
        showModal={openInsertDialog}
        title={t('richTextEditor.insertMetadataField')}
        onClose={() => setOpenInsertDialog(false)}
        autoFocusCloseButton={true}
        dataTestId="rich-text-editor-modal"
        buttons={[
          {
            id: 'cancel-button',
            label: t('common.cancel'),
            dataTestId: 'rte-cancel-button',
            onClick: () => setOpenInsertDialog(false),
            variant: 'secondary',
          },
          {
            id: 'ok-button',
            label: t('common.ok'),
            dataTestId: 'rte-ok-button',
            onClick: insertMetadataField,
            variant: 'primary',
          },
        ]}
      >
        <div data-testid={`arr-rich-text-editor-div`}>
          <div data-testid={`arr-rich-text-editor-div-btn`}>
            <ArrSingleSelectDropdown
              dropdownLabel={''}
              dropdownItems={
                (metadataFieldList || []) as unknown as ISelectItem[]
              }
              selectedValues={selectedMetadataField}
              dropdownItemKey="fieldID"
              dropdownItemDisplay="fieldLabel"
              onSelectionChange={setSelectedMetadataField}
              sizeVariant="large"
              showNoResultsText
              searchable
              formControlRestProps={{
                hiddenLabel: true,
              }}
              dataTestId="asd-selectedmetadatafield"
            />
          </div>
        </div>
      </ArrModal>
    </div>
  );
}
